import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ServiceItem } from '@crm/shared';
import { extractApiError } from '../../lib/api';
import { useGalleryUpload } from './api';

type Props = {
  userId: number;
  services: ServiceItem[];
  onClose: () => void;
};

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];

export function GalleryUploadModal({ userId, services, onClose }: Props): ReactNode {
  const { t } = useTranslation();
  const upload = useGalleryUpload();

  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [serviceId, setServiceId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  // Pogrupowane wg kategorii — UX spójny z resztą panelu.
  const grouped = useMemo(() => {
    const map = new Map<string, ServiceItem[]>();
    for (const s of services) {
      const key = s.category ?? 'Inne';
      const arr = map.get(key) ?? [];
      arr.push(s);
      map.set(key, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [services]);

  const onFileChange = (f: File | null) => {
    setError(null);
    if (preview) URL.revokeObjectURL(preview);
    if (!f) {
      setFile(null);
      setPreview(null);
      return;
    }
    if (!ACCEPTED.includes(f.type)) {
      setError(t('portfolio.upload.errorMime'));
      setFile(null);
      setPreview(null);
      return;
    }
    if (f.size > MAX_BYTES) {
      setError(t('portfolio.upload.errorSize', { mb: Math.round(MAX_BYTES / (1024 * 1024)) }));
      setFile(null);
      setPreview(null);
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError(t('portfolio.upload.errorNoFile'));
      return;
    }
    try {
      await upload.mutateAsync({
        userId,
        file,
        caption: caption.trim() || undefined,
        serviceId: serviceId ? Number(serviceId) : null,
      });
      if (preview) URL.revokeObjectURL(preview);
      onClose();
    } catch (err) {
      setError(extractApiError(err));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/60 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-zinc-900">{t('portfolio.upload.title')}</h2>
        <form onSubmit={onSubmit} className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-700">
              {t('portfolio.upload.file')}
            </label>
            <input
              type="file"
              accept={ACCEPTED.join(',')}
              onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
              className="input mt-1 cursor-pointer"
              required
            />
            <p className="mt-1 text-xs text-zinc-500">
              {t('portfolio.upload.fileHint', { mb: Math.round(MAX_BYTES / (1024 * 1024)) })}
            </p>
          </div>

          {preview && (
            <div className="overflow-hidden rounded-lg bg-zinc-100">
              <img src={preview} alt="" className="max-h-64 w-full object-contain" />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-zinc-700">
              {t('portfolio.upload.caption')}
            </label>
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              maxLength={200}
              className="input mt-1"
              placeholder={t('portfolio.upload.captionPlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700">
              {t('portfolio.upload.service')}
            </label>
            <select
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value)}
              className="input mt-1"
            >
              <option value="">{t('portfolio.upload.serviceNone')}</option>
              {grouped.map(([cat, list]) => (
                <optgroup key={cat} label={cat}>
                  {list.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <p className="mt-1 text-xs text-zinc-500">{t('portfolio.upload.serviceHint')}</p>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={upload.isPending || !file} className="btn-primary">
              {upload.isPending ? t('common.saving') : t('portfolio.upload.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
